import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FiSearch, FiInfo, FiX } from 'react-icons/fi';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background-color: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  gap: 12px;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 12px;
  width: 320px;
  max-width: 100%;

  svg {
    color: #64748b;
    font-size: 16px;
  }

  input {
    border: none;
    background: transparent;
    outline: none;
    width: 100%;
    font-size: 13px;
    color: #0f172a;

    &::placeholder {
      color: #94a3b8;
    }
  }
`;

export const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  width: 100%;
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  white-space: nowrap;
  font-size: 13px;

  th {
    padding: 12px 14px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #1e293b;
    text-align: left;
    position: sticky;
    top: 0;
    background: #f8fafc;
    border-bottom: 2px solid #cbd5e1;
    z-index: 2;
  }

  td {
    padding: 12px 14px;
    font-size: 13px;
    color: #1e293b;
    border-bottom: 1px solid #e2e8f0;
    max-width: 380px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tbody tr {
    transition: background-color 0.15s ease-in-out;
    background-color: #ffffff;

    &:nth-child(even) {
      background-color: #f8fafc;
    }

    &:hover {
      background-color: #f1f5f9;
    }
  }
`;

const Badge = styled.span<{ variant: string }>`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;

  ${({ variant }) => {
    switch (variant) {
      case 'LIC':
        return 'background-color: #e0f2fe; color: #0369a1; border: 1px solid #bae6fd;';
      case 'NE':
        return 'background-color: #d1fae5; color: #047857; border: 1px solid #a7f3d0;';
      case 'NP':
        return 'background-color: #fef3c7; color: #b45309; border: 1px solid #fde68a;';
      case 'XD':
        return 'background-color: #ede9fe; color: #6d28d9; border: 1px solid #ddd6fe;';
      default:
        return 'background-color: #f1f5f9; color: #475569; border: 1px solid #cbd5e1;';
    }
  }}
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #64748b;
  font-size: 14px;
  gap: 12px;

  svg {
    font-size: 32px;
    color: #94a3b8;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #3b82f6;

  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalBox = styled.div`
  background: #ffffff;
  border-radius: 10px;
  width: 600px;
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 700;
  font-size: 15px;
  color: #0f172a;
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  font-family: monospace;
  font-size: 12px;
  background: #f8fafc;
  color: #1e293b;
  white-space: pre-wrap;
  word-break: break-all;
`;

interface ApiDataViewerProps {
  data: any[];
  activeTab?: 'ALL' | 'LIC' | 'NE' | 'NP' | 'XD';
  name?: string;
}

interface ColumnDef {
  key: string;
  header: string;
  render?: (row: any) => React.ReactNode;
}

const formatCurrency = (val: any) => {
  if (val === null || val === undefined || val === '') return '-';
  const num = typeof val === 'number' ? val : parseFloat(String(val).replace(',', '.'));
  if (isNaN(num)) return String(val);
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
};

const formatDate = (val: any) => {
  if (!val) return '-';
  if (typeof val === 'string' && val.includes('/')) return val; // Já está DD/MM/YYYY
  const str = String(val);
  const dateRegex = /^(\d{4})-(\d{2})-(\d{2})/;
  const match = str.match(dateRegex);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  return str;
};

const ApiDataViewer: React.FC<ApiDataViewerProps> = ({ data, activeTab = 'LIC', name }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  // Filtragem de busca em memória
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (!searchTerm.trim()) return data;

    const term = searchTerm.toLowerCase();
    return data.filter((row) => {
      return Object.values(row).some((val) => {
        if (typeof val === 'string' || typeof val === 'number') {
          return String(val).toLowerCase().includes(term);
        }
        return false;
      });
    });
  }, [data, searchTerm]);

  // Definição das colunas especializadas para cada categoria de dados
  const columns: ColumnDef[] = useMemo(() => {
    if (activeTab === 'LIC') {
      return [
        {
          key: 'N_Proc',
          header: 'Nº Processo / Licitação',
          render: (r) => <strong>{r.N_Proc || r.numero_licitacao || '-'}</strong>,
        },
        {
          key: 'Data',
          header: 'Data Homologação',
          render: (r) => formatDate(r.Data || r.data_realizacao_autuacao_licitacao),
        },
        {
          key: 'Modalidade',
          header: 'Modalidade',
          render: (r) => r.Modalidade || '-',
        },
        {
          key: 'Valor',
          header: 'Valor Estimado',
          render: (r) => (
            <span style={{ fontWeight: 600, color: '#0369a1' }}>
              {formatCurrency(r.Valor || r.valor_orcado_estimado)}
            </span>
          ),
        },
        {
          key: 'Vencedor',
          header: 'Vencedor / Fornecedor',
          render: (r) => r.Vencedor || '-',
        },
        {
          key: 'Cod_Forn',
          header: 'CNPJ / CPF',
          render: (r) => r.Cod_Forn || '-',
        },
        {
          key: 'Objeto',
          header: 'Objeto Licitatório',
          render: (r) => (
            <span title={r.Objeto || r.descricao_objeto_licitacao}>
              {r.Objeto || r.descricao_objeto_licitacao || '-'}
            </span>
          ),
        },
      ];
    }

    if (activeTab === 'NE') {
      return [
        {
          key: 'N_Empenho',
          header: 'Nº Empenho',
          render: (r) => <strong>{r.N_Empenho || r.numero_empenho || '-'}</strong>,
        },
        {
          key: 'Ano_Empenho',
          header: 'Exercício',
          render: (r) => r.Ano_Empenho || r.ano_empenho || '-',
        },
        {
          key: 'Data_Empenho',
          header: 'Data Emissão',
          render: (r) => formatDate(r.Data_Empenho || r.Data),
        },
        {
          key: 'Credor',
          header: 'Credor / Fornecedor',
          render: (r) => r.Credor || r.Credor_Nome || '-',
        },
        {
          key: 'Credor_Doc',
          header: 'CNPJ / CPF',
          render: (r) => r.Credor_Doc || '-',
        },
        {
          key: 'Valor',
          header: 'Valor Empenhado',
          render: (r) => (
            <span style={{ fontWeight: 600, color: '#047857' }}>
              {formatCurrency(r.Valor || r.valor_empenhado)}
            </span>
          ),
        },
        {
          key: 'N_Proc',
          header: 'Licitação Vinculada',
          render: (r) => r.N_Proc || r.numero_licitacao || '-',
        },
        {
          key: 'CodOrg',
          header: 'Órgão / UG',
          render: (r) => (r.CodOrg || r.CodUG ? `${r.CodOrg || ''} / ${r.CodUG || ''}` : '-'),
        },
        {
          key: 'Objeto',
          header: 'Histórico da Despesa',
          render: (r) => (
            <span title={r.Objeto || r.descricao_historico_empenho}>
              {r.Objeto || r.descricao_historico_empenho || '-'}
            </span>
          ),
        },
      ];
    }

    if (activeTab === 'NP') {
      return [
        {
          key: 'Doc_Caixa',
          header: 'Nº Doc. Caixa',
          render: (r) => <strong>{r.Doc_Caixa || r.numero_documento_caixa || '-'}</strong>,
        },
        {
          key: 'Data',
          header: 'Data Pagamento',
          render: (r) => formatDate(r.Data || r.data_nota_pagamento),
        },
        {
          key: 'N_Empenho',
          header: 'Nº Empenho',
          render: (r) => r.N_Empenho || r.numero_empenho || '-',
        },
        {
          key: 'Ano_Empenho',
          header: 'Ano Empenho',
          render: (r) =>
            r.Ano_Empenho ||
            r.ano_empenho ||
            (r.data_emissao_empenho ? String(r.data_emissao_empenho).substring(0, 4) : '') ||
            (r.data_nota_pagamento ? String(r.data_nota_pagamento).substring(0, 4) : '') ||
            '-',
        },
        {
          key: 'Valor',
          header: 'Valor Pago',
          render: (r) => (
            <span style={{ fontWeight: 600, color: '#b45309' }}>
              {formatCurrency(r.Valor ?? r.valor_nota_pagamento)}
            </span>
          ),
        },
        {
          key: 'Responsavel',
          header: 'Responsável',
          render: (r) => r.Responsavel || r.nome_responsavel_pagamento || '-',
        },
        {
          key: 'CodOrg',
          header: 'Órgão / UG',
          render: (r) => {
            const org = r.CodOrg || r.codigo_orgao;
            const ug = r.CodUG || r.codigo_unidade_orcamentaria;
            return org || ug ? `${org || ''} / ${ug || ''}` : '-';
          },
        },
      ];
    }

    if (activeTab === 'XD') {
      return [
        {
          key: 'Doc_Caixa',
          header: 'Nº Doc. Caixa',
          render: (r) => <strong>{r.Doc_Caixa || '-'}</strong>,
        },
        {
          key: 'Data',
          header: 'Data',
          render: (r) => formatDate(r.Data),
        },
        {
          key: 'Credor',
          header: 'Credor / Favorecido',
          render: (r) => r.Credor || '-',
        },
        {
          key: 'Valor',
          header: 'Valor',
          render: (r) => (
            <span style={{ fontWeight: 600, color: '#6d28d9' }}>
              {formatCurrency(r.Valor)}
            </span>
          ),
        },
        {
          key: 'Objeto',
          header: 'Descrição / Histórico',
          render: (r) => r.Objeto || '-',
        },
      ];
    }

    // Default 'ALL': Visão consolidada
    return [
      {
        key: 'tipoRegistro',
        header: 'Tipo',
        render: (r) => <Badge variant={r.tipoRegistro || 'OTHER'}>{r.tipoRegistro || 'OUTRO'}</Badge>,
      },
      {
        key: 'identificador',
        header: 'Identificador Principal',
        render: (r) => <strong>{r.N_Proc || r.N_Empenho || r.Doc_Caixa || '-'}</strong>,
      },
      {
        key: 'Data',
        header: 'Data',
        render: (r) => formatDate(r.Data || r.Data_Empenho),
      },
      {
        key: 'Entidade',
        header: 'Credor / Fornecedor',
        render: (r) => r.Credor || r.Vencedor || r.Responsavel || '-',
      },
      {
        key: 'Valor',
        header: 'Valor',
        render: (r) => <span style={{ fontWeight: 600 }}>{formatCurrency(r.Valor)}</span>,
      },
      {
        key: 'Objeto',
        header: 'Descrição / Objeto',
        render: (r) => <span title={r.Objeto}>{r.Objeto || '-'}</span>,
      },
    ];
  }, [activeTab]);

  return (
    <Container>
      <Toolbar>
        <SearchInputWrapper>
          <FiSearch />
          <input
            type="text"
            placeholder="Buscar por número, credor, objeto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInputWrapper>

        {name && (
          <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
            {name}
          </span>
        )}
      </Toolbar>

      <TableContainer>
        {filteredData.length === 0 ? (
          <EmptyState>
            <FiInfo />
            <span>Nenhum registro encontrado nesta categoria ou filtro.</span>
          </EmptyState>
        ) : (
          <StyledTable>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.header}</th>
                ))}
                <th style={{ width: 80, textAlign: 'center' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, idx) => (
                <tr key={row._id || idx}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(row) : String(row[col.key] ?? '-')}
                    </td>
                  ))}
                  <td style={{ textAlign: 'center' }}>
                    <ActionButton
                      type="button"
                      title="Ver dados brutos"
                      onClick={() => setSelectedItem(row)}
                    >
                      Detalhes
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        )}
      </TableContainer>

      {/* Modal de Detalhes Brutos */}
      {selectedItem && (
        <ModalOverlay onClick={() => setSelectedItem(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <span>Detalhes do Registro</span>
              <button
                type="button"
                onClick={() => setSelectedItem(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
              >
                <FiX />
              </button>
            </ModalHeader>
            <ModalBody>
              {JSON.stringify(selectedItem, null, 2)}
            </ModalBody>
          </ModalBox>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ApiDataViewer;
